var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Expose } from 'class-transformer';
import { IsEnum, IsOptional, IsString, MaxLength } from 'class-validator';
import { GkvInsuranceProvenance } from '../enums/gkv-insurance-provenance.enum.js';
import { VERSICHERTENSTATUS_MAX_LENGTH } from '../value-objects/versichertenstatus.js';
/**
 * The eGK snapshot behind a GKV Verordnung (#1503) — what the card said when
 * **this** plan was issued.
 *
 * ⚠ **No Kassenname field, on the wire or anywhere else.** The Krankenkasse is
 * the card IK alone; the name is resolved from the Kostenträger corpus for
 * display (#1507).
 *
 * ⚠ Nothing here relates to `pt_patient_record.krankenkasse`, the patient's own
 * free-text „Versicherung". Different author, different grain, different shape.
 *
 * Everything is optional and nullable, and stays that way: capture is soft. A
 * missing or implausible Kostenträgerkennung warns in the UI and saves. The hard
 * refusal belongs to the Abrechnungslauf's candidate selection (TM-9538).
 */
export class GkvInsuranceDataDto {
    /**
     * Kostenträgerkennung — the **card IK** (Muster 13 field 08), the input to
     * `resolveKostentraegerRouting`, not the IK it resolves to.
     */
    kostentraegerCode;
    /** Versichertennummer (Muster 13 field 09). Alphanumeric. */
    versichertenId;
    /**
     * Versichertenart — Stelle 1 of the printed `Status`, Muster 13 field 10
     * (#1506). `1` Mitglied · `3` Familienversicherter · `5` Rentner.
     *
     * ⚠ Not validated against that set here. A value outside it warns in the UI
     * and saves: it lands in Summenstatus `99`, a *defined* bucket, and drags a
     * NAD-Anschrift requirement with it — the §302 file stays valid. Refusing it
     * would be stricter than the spec and would hold up revenue.
     */
    versichertenart;
    /** BesonderePersonengruppe — Stellen 2–3, Muster 13 field 11 (#1506). */
    besonderePersonengruppe;
    /** DMP_Kennzeichnung — Stellen 4–5, Muster 13 field 12 (#1506). */
    dmpKennzeichnung;
    /**
     * The `Status` box as it was read off the Verordnung (#1506), kept
     * **alongside** the three derived parts — §302 asks for a transcription and a
     * handwritten status is legitimate.
     *
     * ⚠ **Alphanumeric, never digits-only.** `INV.Versichertenstatus` is typed
     * `AN` because the retired KVK Stelle 5 could be a letter. Seven characters
     * is the printed box (Stellen 1–7 of Druckzeile 6).
     */
    versichertenstatus;
    /**
     * Where this card came from (#1508) — `scanned` off the Muster-13 barcode or
     * `manual` off the sheet. Omitted on an update, the stored value stands: a
     * corrected scan is still a scan, and a Therapeut:in fixing a mistyped digit
     * must not silently erase how the card reached us.
     *
     * ⚠ This is the **only** enum-typed field in the block, and it is ours, not
     * the KBV's. Everything else here is a published value set that warns and
     * saves; provenance is a closed vocabulary we author, so it may be refused.
     */
    provenance;
}
__decorate([
    Expose(),
    IsString(),
    MaxLength(20),
    IsOptional(),
    __metadata("design:type", Object)
], GkvInsuranceDataDto.prototype, "kostentraegerCode", void 0);
__decorate([
    Expose(),
    IsString(),
    MaxLength(30),
    IsOptional(),
    __metadata("design:type", Object)
], GkvInsuranceDataDto.prototype, "versichertenId", void 0);
__decorate([
    Expose(),
    IsString(),
    MaxLength(2),
    IsOptional(),
    __metadata("design:type", Object)
], GkvInsuranceDataDto.prototype, "versichertenart", void 0);
__decorate([
    Expose(),
    IsString(),
    MaxLength(2),
    IsOptional(),
    __metadata("design:type", Object)
], GkvInsuranceDataDto.prototype, "besonderePersonengruppe", void 0);
__decorate([
    Expose(),
    IsString(),
    MaxLength(2),
    IsOptional(),
    __metadata("design:type", Object)
], GkvInsuranceDataDto.prototype, "dmpKennzeichnung", void 0);
__decorate([
    Expose(),
    IsString(),
    MaxLength(VERSICHERTENSTATUS_MAX_LENGTH),
    IsOptional(),
    __metadata("design:type", Object)
], GkvInsuranceDataDto.prototype, "versichertenstatus", void 0);
__decorate([
    Expose(),
    IsEnum(GkvInsuranceProvenance),
    IsOptional(),
    __metadata("design:type", String)
], GkvInsuranceDataDto.prototype, "provenance", void 0);
export class GkvInsuranceDataResponseDto {
    id;
    createdAt;
    updatedAt;
    kostentraegerCode;
    versichertenId;
    versichertenart;
    besonderePersonengruppe;
    dmpKennzeichnung;
    versichertenstatus;
    provenance;
}
//# sourceMappingURL=gkv-insurance-data.dto.js.map